import "./App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { analytics, db } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { logEvent } from "firebase/analytics";
import { Analytics } from "@vercel/analytics/react";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import CollegeSelectionPage from "./pages/CollegeSelectionPage";
import OnboardingPage from "./pages/OnboardingPage";
import WebAppHome from "./pages/WebAppHome";
import AnonymousChatSetupPage from "./pages/AnonymousChatSetupPage";
import FindingMatchPage from "./pages/FindingMatchPage";
import ChatPage from "./pages/ChatPage";
import SavedChatsPage from "./pages/SavedChatsPage";
import SavedPostsPage from "./pages/SavedPostsPage";
import ModeSelectionPage from "./pages/ModeSelectionPage";
import VoiceSetupPage from "./pages/VoiceSetupPage";
import VoiceCallPage from "./pages/VoiceCallPage";
import GroupVoiceSetupPage from "./pages/GroupVoiceSetupPage";
import GroupVoiceRoomPage from "./pages/GroupVoiceRoomPage";
import CreateRoomPage from "./pages/CreateRoomPage";
import JoinRoomPage from "./pages/JoinRoomPage";
import CustomRoomsHub from "./pages/CustomRoomsHub";
import CustomVoiceRoomPage from "./pages/CustomVoiceRoomPage";
import ViewPostPage from "./pages/ViewPostPage";
import { ShadowPostView } from "./pages/ShadowPostView";
import { ShadowProfileScreen } from "./pages/ShadowProfileScreen";
import { SettingsLayout } from "./pages/SettingsLayout";
import { PersonalDetailsPage } from "./pages/Settings/PersonalDetailsPage";
import { AccountManagementPage } from "./pages/Settings/AccountManagementPage";
import { ShadowsLockPage } from "./pages/Settings/ShadowsLockPage";
import { NotificationsSettingsPage } from "./pages/Settings/NotificationsSettingsPage";
import { PushNotificationsSettingsPage } from "./pages/Settings/PushNotificationsSettingsPage";
import { InAppNotificationsSettingsPage } from "./pages/Settings/InAppNotificationsSettingsPage";
import { AdvancedSettingsPage } from "./pages/Settings/AdvancedSettingsPage";
import { DeleteAccountPage } from "./pages/Settings/DeleteAccountPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { CreateShadow } from "./pages/CreateShadow";
import { CreatorProgram } from "./pages/CreatorProgram";
import ShadowChatLayout from "./pages/ShadowChatLayout";
import { ShadowWeeklyLead } from "./pages/ShadowWeeklyLead";
import { SparkPremium } from "./pages/Premium/SparkPremium";
import { IdentityProvider } from "./context/IdentityContext";
import { PopupProvider } from "./context/PopupContext";
import { NavigationCacheProvider } from "./context/NavigationCacheContext";
import { AppLayout } from "./components/Layout/AppLayout";
import { Toaster } from "sonner";
import { PremiumProtectedRoute } from "./components/Premium/PremiumProtectedRoute";
import { ServiceModal } from "./components/ServiceModal";

function App() {
  const [activeGlobalServiceMode, setActiveGlobalServiceMode] = useState(null);

  useEffect(() => {
    // Global service listener
    const unsubscribe = onSnapshot(doc(db, 'version', 'availability_info'), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.NAatTheMoment === true) {
          setActiveGlobalServiceMode('MAINTENANCE');
        } else if (data.Sparking === true) {
          setActiveGlobalServiceMode('TRAFFIC');
        } else {
          setActiveGlobalServiceMode(null);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Log app init
    logEvent(analytics, 'app_initialized', {
      platform: 'web'
    });
  }, []);

  return (
    <PopupProvider>
      <IdentityProvider>
        <NavigationCacheProvider>
          <BrowserRouter>
          <Toaster theme="dark" position="bottom-center" />
          <Routes>
          <Route path="/info" element={<LandingPage />} />
          <Route path="/info/:section" element={<LandingPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/college-selection" element={<CollegeSelectionPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />

          {/* Web App Routes Wrapped in Persistent Layout */}
          <Route element={<AppLayout />}>
            <Route path="/" element={<WebAppHome />} />
            
            {/* New Split View Chat Route */}
            <Route path="/chat" element={<ShadowChatLayout />}>
              <Route index element={<AnonymousChatSetupPage />} />
              <Route path="searching" element={<FindingMatchPage />} />
              <Route path="room/:id" element={<ChatPage />} />
            </Route>
            
            <Route path="/create-shadow" element={<CreateShadow />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/saved-chats" element={<SavedChatsPage />} />
            <Route path="/saved-posts" element={<SavedPostsPage />} />
            
            {/* Selection Routes */}
            <Route path="/mode-select" element={<ModeSelectionPage />} />
            <Route path="/find-voice-match-search" element={
              <PremiumProtectedRoute><VoiceSetupPage /></PremiumProtectedRoute>
            } />
            <Route path="/voice-chat/:roomId" element={
              <PremiumProtectedRoute><VoiceCallPage /></PremiumProtectedRoute>
            } />
            <Route path="/group-voice-setup" element={
              <PremiumProtectedRoute><GroupVoiceSetupPage /></PremiumProtectedRoute>
            } />
            <Route path="/group-voice-room/:roomId" element={
              <PremiumProtectedRoute><GroupVoiceRoomPage /></PremiumProtectedRoute>
            } />
            <Route path="/create-room" element={<CreateRoomPage />} />
            <Route path="/join-room" element={<JoinRoomPage />} />
            <Route path="/custom-rooms" element={<CustomRoomsHub />} />
            <Route path="/room/:code" element={<JoinRoomPage />} />
            <Route path="/web/room/:code" element={<JoinRoomPage />} />
            <Route path="/custom-voice-room/:roomId" element={<CustomVoiceRoomPage />} />
            <Route path="/web/custom-voice-room/:roomId" element={<CustomVoiceRoomPage />} />
            <Route path="/post/:id" element={<ViewPostPage />} />
            <Route path="/shadow/:id" element={<ShadowPostView />} />
            <Route path="/shadow-leaderboard" element={<ShadowWeeklyLead />} />
            <Route path="/shadow-profile" element={<ShadowProfileScreen />} />
            {/* <Route path="/premium" element={<SparkPremium />} /> */}
            {/* Settings & Creator Program */}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<PersonalDetailsPage />} />
              <Route path="personal" element={<PersonalDetailsPage />} />
              <Route path="account" element={<AccountManagementPage />} />
              <Route path="shadows-lock" element={<ShadowsLockPage />} />
              <Route path="notifications" element={<NotificationsSettingsPage />} />
              <Route path="notifications/push" element={<PushNotificationsSettingsPage />} />
              <Route path="notifications/in-app" element={<InAppNotificationsSettingsPage />} />
              <Route path="advanced" element={<AdvancedSettingsPage />} />
              <Route path="delete-account" element={<DeleteAccountPage />} />
              <Route path="creator-program" element={<CreatorProgram />} />
            </Route>
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Analytics />
        </BrowserRouter>
        <ServiceModal visible={activeGlobalServiceMode !== null} type={activeGlobalServiceMode} />
      </NavigationCacheProvider>
    </IdentityProvider>
  </PopupProvider>
);
}

export default App;
